import { useEffect, useState, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Upload, FileText, X, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const empty = { title: "", category: "", summary: "", justification: "", technical_notes: "", template_text: "" };

export default function AdminBancoIdeias() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/ideas").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      let saved;
      if (editId) {
        const { data } = await api.put(`/admin/ideas/${editId}`, form);
        saved = data;
      } else {
        const { data } = await api.post("/admin/ideas", form);
        saved = data;
        setEditId(saved.id);
      }
      setEditingDoc(saved);
      toast.success("Salvo! Agora você pode anexar o arquivo Word do PL.");
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const edit = (i) => {
    setEditId(i.id);
    setForm({
      title: i.title || "",
      category: i.category || "",
      summary: i.summary || "",
      justification: i.justification || "",
      technical_notes: i.technical_notes || "",
      template_text: i.template_text || "",
    });
    setEditingDoc(i);
    setOpen(true);
  };

  const remove = async (id) => {
    if (window.confirm("Excluir esta PL/ideia?")) {
      await api.delete(`/admin/ideas/${id}`);
      load();
    }
  };

  const onClose = () => {
    setOpen(false);
    setEditId(null);
    setEditingDoc(null);
    setForm(empty);
  };

  const onPickFile = () => fileRef.current?.click();

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Arquivo maior que 20 MB.");
      e.target.value = "";
      return;
    }
    if (!editId) {
      toast.error("Salve a PL primeiro, depois anexe o arquivo.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/ideas/${editId}/upload-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditingDoc(data);
      toast.success(`Arquivo enviado (${data.file_size_kb || "?"} KB)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onRemoveFile = async () => {
    if (!editId) return;
    if (!window.confirm("Remover o arquivo anexado?")) return;
    try {
      const { data } = await api.delete(`/admin/ideas/${editId}/file`);
      setEditingDoc(data);
      load();
      toast.success("Arquivo removido");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const downloadFile = async (id, fileName) => {
    try {
      const { data } = await api.get(`/ideas/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "projeto-de-lei.docx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Erro ao baixar arquivo.");
    }
  };

  return (
    <AdminLayout title="Banco de PLs">
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditId(null); setForm(empty); setEditingDoc(null); setOpen(true); }} data-testid="new-idea-btn">
          <Plus className="w-4 h-4 mr-1" /> Novo Projeto de Lei
        </Button>
      </div>
      <Card className="rounded-xl"><CardContent className="p-0 overflow-x-auto">
        <Table><TableHeader><TableRow>
          <TableHead>Título</TableHead>
          <TableHead>Categoria</TableHead>
          <TableHead>Resumo</TableHead>
          <TableHead className="text-center">Arquivo</TableHead>
          <TableHead className="text-right">Ações</TableHead>
        </TableRow></TableHeader>
          <TableBody>
            {items.map(i => (
              <TableRow key={i.id} data-testid={`row-idea-${i.id}`}>
                <TableCell className="font-medium">{i.title}</TableCell>
                <TableCell>{i.category && <Badge variant="secondary">{i.category}</Badge>}</TableCell>
                <TableCell className="text-xs text-muted-foreground line-clamp-2 max-w-md">{i.summary}</TableCell>
                <TableCell className="text-center">
                  {i.has_file ? (
                    <Button variant="ghost" size="sm" onClick={() => downloadFile(i.id, i.file_name)} data-testid={`download-${i.id}`}>
                      <FileText className="w-4 h-4 mr-1 text-emerald-600" />
                      <span className="text-xs text-emerald-700">Baixar</span>
                    </Button>
                  ) : (
                    <span className="text-[11px] text-muted-foreground italic">Sem arquivo</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => edit(i)} data-testid={`edit-${i.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(i.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Nenhuma PL cadastrada</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-[640px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar Projeto de Lei" : "Novo Projeto de Lei"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Título *</Label>
              <Input required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: PL — Acessibilidade em logradouros públicos" data-testid="idea-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Saúde, Educação, Mobilidade…" data-testid="idea-category" />
              </div>
            </div>
            <div>
              <Label>Resumo *</Label>
              <Textarea required rows={3} value={form.summary} onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))} placeholder="Breve descrição do que o PL propõe (1-3 linhas)" data-testid="idea-summary" />
            </div>
            <div>
              <Label>Justificativa (opcional)</Label>
              <Textarea rows={2} value={form.justification} onChange={(e) => setForm(f => ({ ...f, justification: e.target.value }))} placeholder="Por que esse PL é importante" />
            </div>
            <div>
              <Label>Notas técnicas (opcional)</Label>
              <Textarea rows={2} value={form.technical_notes} onChange={(e) => setForm(f => ({ ...f, technical_notes: e.target.value }))} placeholder="Aspectos jurídicos, orçamentários ou processuais relevantes" />
            </div>

            <Button type="submit" className="w-full" data-testid="idea-save">
              {editId ? "Salvar alterações" : "Cadastrar PL"}
            </Button>
          </form>

          {/* Upload de arquivo — só aparece DEPOIS de salvo */}
          {editId && (
            <Card className="border-2 border-dashed mt-4" data-testid="idea-file-card">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Arquivo do Projeto de Lei</Label>
                  {editingDoc?.has_file && (
                    <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50">
                      <FileText className="w-3 h-3 mr-1" />
                      {editingDoc.file_name || "arquivo"} · {editingDoc.file_size_kb || "?"} KB
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Anexe o arquivo em <strong>Word (.docx ou .doc)</strong> ou <strong>PDF</strong>. Limite: 20 MB.
                  O aluno poderá baixar esse arquivo direto no app.
                </p>
                <input
                  type="file"
                  accept=".docx,.doc,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf"
                  ref={fileRef}
                  className="hidden"
                  onChange={onUploadFile}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={onPickFile}
                    disabled={uploading}
                    variant="outline"
                    className="flex-1"
                    data-testid="idea-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading ? "Enviando…" : (editingDoc?.has_file ? "Substituir arquivo" : "Enviar arquivo")}
                  </Button>
                  {editingDoc?.has_file && (
                    <>
                      <Button type="button" variant="outline" size="icon" onClick={() => downloadFile(editId, editingDoc.file_name)} aria-label="Baixar atual">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" onClick={onRemoveFile} className="text-rose-600" aria-label="Remover arquivo">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {!editId && (
            <p className="text-xs text-muted-foreground italic mt-2">
              💡 Para anexar o arquivo Word do PL, primeiro clique em "Cadastrar PL" acima — em seguida o campo de upload aparecerá.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
